import { FadedScrollerContainer, Scroller } from '@/components/shared';
import { fetchAllStepProgressForThread } from '@/store/thunks/FetchStepProgress';
import { fetchLatestReportState } from '@/store/thunks/FetchReport';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getReportStateForThread } from '@/store/reducers/ReportWidgetStore';
import { getStepProgressListFromStateByMessageId } from '@/store/reducers/StepProgressStore';
import { MessageSteps } from '@/components/chat/MessageSteps';
import { mkDownloadReportBtnTrackAttrs } from '@/analytics/props/corpusqa';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { removeIrrelevantFields } from '@/models/PanelWidgets';
import { Report } from '@/components/report/Report';
import { StepRunState } from '@/models/StepProgress';
import { Thread } from '@/models/Thread';
import { ThreadFeedbackButtons } from '@/components/chat/ThreadFeedbackButtons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
} from '@mui/material';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import styled from 'styled-components';

type CorpusQAChatProps = {
  thread: Thread;
};

const REPORT_CREATION_TIMEOUT = 300000; // 5 minutes
const INITIAL_DISPLAY_TIMEOUT = 30000; // 30 seconds

export const CorpusQAChat = ({ thread }: CorpusQAChatProps) => {
  const { isReadOnly } = useContext(PermissionsContext);
  const dispatch = useAppDispatch();
  const conversationMessages = thread.messages;
  const reportData = useAppSelector((state) =>
    getReportStateForThread(state, thread.id),
  );
  const [isDisclaimerOpen, setisDisclaimerOpen] = useState(false);
  const [hasInitialDisplayTimedOut, setHasInitialDisplayTimedOut] =
    useState(false);
  const [hasReportCreationTimedOut, setHasReportCreationTimedOut] =
    useState(false);

  const openDisclaimerModal = (): void => {
    setisDisclaimerOpen(true);
  };

  const closeDisclaimerModal = (): void => {
    setisDisclaimerOpen(false);
  };

  const downloadReportData = (): void => {
    //  Remove fields that are not relevant to users
    const reportDataWithoutIrrelevantFields =
      removeIrrelevantFields(reportData);

    const jsonString = JSON.stringify(reportDataWithoutIrrelevantFields);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const now = new Date().toISOString();
    a.download = `report-${now}.json`;
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  // based on assumption this is a single turn conversation
  const firstMessage = conversationMessages[0];

  const stepProgressList = useAppSelector((state) => {
    return getStepProgressListFromStateByMessageId(
      state.stepProgressStore,
      firstMessage?.id,
    );
  });

  const failedStep = stepProgressList.find(
    (stepProgress) => stepProgress.runState === StepRunState.FAILED,
  );

  const isCreatingReport = !!stepProgressList.find(
    (stepProgress) => stepProgress.runState !== StepRunState.SUCCEEDED,
  );

  useEffect(() => {
    dispatch(fetchLatestReportState(thread.id));
  }, [thread.id]);

  // Covers 2 cases:
  // Error outside corpusQA tool - no step progress and no report data after 30s
  // Error inside corpusQA tool - report stuck in progress state for 5 minutes
  useEffect(() => {
    const initialDisplayTimeout = setTimeout(() => {
      getLayoverLogger().logError(
        'Could not find step progress nor report widget after 60s time out',
      );
      setHasInitialDisplayTimedOut(true);
    }, INITIAL_DISPLAY_TIMEOUT);

    const reportCreationTimeout = setTimeout(() => {
      getLayoverLogger().logError(
        'Report stuck in progress state for 5 minutes',
      );
      // refetch all step progress in case we missed the some final 'SUCCEEDED' events through pubsub
      // before we flag the report as stuck
      dispatch(fetchAllStepProgressForThread(thread.id));
      setHasReportCreationTimedOut(true);
    }, REPORT_CREATION_TIMEOUT);
    return () => {
      clearTimeout(initialDisplayTimeout);
      clearTimeout(reportCreationTimeout);
    };
  }, []);

  const renderSkeleton = (): ReactNode => (
    <Fragment>
      <ReportSkeleton variant="rounded" height={60} />
      <ReportSkeleton variant="rounded" height={60} />
      <ReportSkeleton variant="rounded" height={60} />
    </Fragment>
  );

  // show Error when there is no step progress and no report data after 60s. This means
  // there was an error calling CorpusQA tool or the tool broke before emitting any step progress
  const hasNoInitialDisplay =
    hasInitialDisplayTimedOut && !reportData && !stepProgressList.length;

  // show Error when the report is stuck in progress state for 5 minutes
  const isStepProgressStuck = hasReportCreationTimedOut && isCreatingReport;

  // show skeleton loading state when not all steps have 'SUCCEEDED' state and there are no other timeout errors or step progress errors
  const shouldRenderSkeleton =
    isCreatingReport &&
    !hasNoInitialDisplay &&
    !failedStep &&
    !isStepProgressStuck;

  return (
    <Fragment>
      <FadedScrollerContainer>
        <Scroller>
          <Question>{firstMessage?.text}</Question>

          {failedStep && (
            <Alert variant="filled" severity="error" sx={{ mb: 1.5 }}>
              {failedStep.errorMessage}
            </Alert>
          )}

          {hasNoInitialDisplay && (
            <Alert variant="filled" severity="error" sx={{ mb: 1.5 }}>
              Something went wrong - please try a different query
            </Alert>
          )}

          {isStepProgressStuck && !failedStep && !hasNoInitialDisplay && (
            <Alert variant="filled" severity="warning" sx={{ mb: 1.5 }}>
              Generating your report is taking longer than usual. Please stay
              patient, or try again later.
            </Alert>
          )}

          {!!firstMessage && isCreatingReport && (
            <Box mb={1.5}>
              <MessageSteps message={firstMessage} renderUserIcon={false} />
            </Box>
          )}

          {!!reportData && !isReadOnly && !isCreatingReport && (
            <ThreadFeedback>
              Are these answers helpful?
              <ThreadFeedbackButtons threadId={thread.id} />
              <ThreadFeedbackActions>
                <ActionButton
                  onClick={downloadReportData}
                  size="small"
                  {...mkDownloadReportBtnTrackAttrs({
                    threadId: thread.id,
                    widgetId: reportData.id,
                  })}
                >
                  <DownloadIcon />
                  Download Report
                </ActionButton>
                •
                <ActionButton
                  variant="text"
                  onClick={openDisclaimerModal}
                  size="small"
                >
                  Disclaimer
                </ActionButton>
              </ThreadFeedbackActions>
            </ThreadFeedback>
          )}
          <Box mt={1.5}>
            <Report />
            {shouldRenderSkeleton && renderSkeleton()}
          </Box>
        </Scroller>
      </FadedScrollerContainer>
      <Dialog open={isDisclaimerOpen} onClose={closeDisclaimerModal}>
        <DisclaimerTitle>Disclaimer</DisclaimerTitle>
        <DialogContent>
          Ai2 Scholar QA answers questions by retrieving open-access papers from
          the scientific literature. It is not designed to answer non-scientific
          questions or questions that require sources outside the scientific
          literature. Its output may have errors, and these errors might be
          difficult to detect. For example, there might be serious factual
          inaccuracies or omissions. Please verify the accuracy of the generated
          text whenever possible.
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

const Question = styled('h3')`
  color: ${({ theme }) => theme.palette.text.reversed};
`;

const ThreadFeedback = styled('div')`
  align-items: center;
  color: ${({ theme }) => theme.palette.text.reversed};
  background: rgba(250, 242, 233, 0.04);
  border: 1px solid rgba(250, 242, 233, 0.1);
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: flex-start;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

const ThreadFeedbackActions = styled('div')`
  align-items: center;
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.spacing(0.5)};
  justify-content: flex-end;
`;

const DisclaimerTitle = styled(DialogTitle)`
  &.MuiDialogTitle-root {
    color: ${({ theme }) => theme.color['dark-teal-100'].hex};
    font-weight: 500;
    font-size: 24px;
    line-height: ${({ theme }) => theme.spacing(3.5)};
  }
`;

const ActionButton = styled(Button)`
  &.MuiButtonBase-root {
    color: ${({ theme }) => theme.palette.secondary.main};
    padding: 0;
  }

  & .MuiSvgIcon-root {
    font-size: 1rem;
    margin-right: ${({ theme }) => theme.spacing(0.25)};
  }
`;

const ReportSkeleton = styled(Skeleton)`
  &.MuiSkeleton-root {
    background: ${({ theme }) => alpha(theme.color['cream-100'].hex, 0.2)};
    height: 80px;
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;
